import { Button } from "@promptwatch/ui/components/button";
import { Card, CardContent } from "@promptwatch/ui/components/card";
import { ArrowRight, Bot, CircleFadingArrowUp, Eye, FileText, Lightbulb, type LucideIcon, Telescope } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import BaseLayout from "@/components/base-layout";
import { Header } from "@/components/header";
import { clientEnv } from "@/env/client";

interface ToolCardData {
  icon: LucideIcon;
  title: string;
  description: string;
  href: string;
  buttonText: string;
}

const toolsData: ToolCardData[] = [
  {
    icon: Telescope,
    title: "AI Brand Visibility Report",
    description:
      "Discover how well AI assistants like ChatGPT, Claude, and Perplexity know your brand. Get detailed visibility scores, sentiment analysis, and competitive insights across multiple AI models. See which competitors are mentioned alongside your brand and understand your share of voice in AI responses.",
    href: "/free-tools/ai-brand-visibility-report",
    buttonText: "Analyze Your Brand",
  },
  {
    icon: CircleFadingArrowUp,
    title: "AI SEO Audit",
    description:
      "Instantly analyze your website's optimization for ChatGPT, Claude, Perplexity, and Gemini. Our free AI SEO audit checks if your site appears correctly when users ask ChatGPT or Claude about your industry. Get a comprehensive AI visibility score and actionable insights to improve how AI assistants present your business.",
    href: "/free-tools/ai-seo-audit",
    buttonText: "Audit Your Site",
  },
  {
    icon: FileText,
    title: "Website LLMs.txt Generator",
    description:
      "Create an optimized LLMs.txt file that helps ChatGPT, Claude, Perplexity, and Gemini understand your business. This file ensures AI models accurately recommend your products when users ask questions. Improve your ChatGPT search visibility and Claude AI recommendations with structured data AI assistants can understand.",
    href: "/free-tools/llms-txt-generator",
    buttonText: "Generate LLMs.txt",
  },
  {
    icon: Eye,
    title: "AI Crawler URL Inspector",
    description:
      "Discover exactly how AI models like ChatGPT, Claude, and Perplexity see your website. View the extracted markdown content and metadata that LLMs use to understand and recommend your pages.",
    href: "/free-tools/ai-crawler-url-inspector",
    buttonText: "Inspect Your Site",
  },
  {
    icon: Lightbulb,
    title: "Free Tool Idea Generator",
    description:
      "Get unlimited AI-generated free tool ideas tailored to your website and business. Analyze your site to discover innovative tool concepts that will engage your audience, drive traffic, and showcase your expertise in your industry.",
    href: "/free-tools/free-tool-idea-generator",
    buttonText: "Generate Ideas",
  },
  {
    icon: Bot,
    title: "Robots.txt Generator for AI",
    description:
      "Create a robots.txt file that allows AI models like ChatGPT, Claude, and Perplexity to crawl your website. This file helps AI models understand your website and recommend your pages.",
    href: "/free-tools/robots-txt-generator-for-ai",
    buttonText: "Generate Robots.txt",
  },
];

const ToolCard = ({ tool }: { tool: ToolCardData }) => {
  const IconComponent = tool.icon;

  return (
    <Link href={tool.href} className="block">
      <Card className="group hover:shadow-lg transition-shadow cursor-pointer h-full">
        <CardContent className="p-6">
          <div className="flex flex-col items-start gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <IconComponent className="size-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold mb-2">{tool.title}</h3>
              <p className="text-muted-foreground text-sm mb-4 line-clamp-3">{tool.description}</p>
              <div className="inline-flex items-center text-blue-600 font-medium group-hover:text-blue-800 transition-colors">
                {tool.buttonText} <ArrowRight className="size-4 ml-2" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const FreeToolsIndex = () => {
  const posthog = usePostHog();
  const router = useRouter();

  return (
    <BaseLayout>
      <Header
        title="Free AI SEO Tools by Promptwatch.com | ChatGPT, Claude & Perplexity Optimization"
        description="Free AI SEO tools by Promptwatch for ChatGPT, Claude, Perplexity & Gemini optimization. Check your website's AI visibility score, generate LLMs.txt files, and improve your rankings in ChatGPT search, Claude Projects, and AI-powered search results."
      />
      <main className="overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-200/50 via-blue-100/50" />
        <div
          aria-hidden="true"
          className="absolute h-full w-full scale-[0.8] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#fff_70%,transparent_100%)]"
        />

        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11 z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11 z-40" />
            <div className="relative border-x border-gray-950/[0.07]">
              <nav aria-label="Breadcrumb" className="p-3">
                <ol className="flex items-center text-sm">
                  <li>
                    <Link href="/" className="text-primary hover:underline">
                      Home
                    </Link>
                  </li>
                  <li className="mx-2 text-muted-foreground">/</li>
                  <li className="text-gray-700" aria-current="page">
                    Free Tools
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </section>

        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11 z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11 z-40" />
            <div className="relative border-x border-gray-950/[0.07] px-4 pb-16 pt-16 sm:px-12">
              <div className="relative w-fit bg-gray-950/5 p-2 mb-6">
                <div aria-hidden="true" className="absolute left-1 top-1 size-[3px] rounded-full bg-gray-950/20" />
                <div aria-hidden="true" className="absolute right-1 top-1 size-[3px] rounded-full bg-gray-950/20" />
                <div aria-hidden="true" className="absolute bottom-1 left-1 size-[3px] rounded-full bg-gray-950/20" />
                <div aria-hidden="true" className="absolute bottom-1 right-1 size-[3px] rounded-full bg-gray-950/20" />
                <div className="relative flex h-fit items-center gap-2 rounded-full bg-white px-3 py-1 shadow">
                  <span className="text-title text-sm flex items-center gap-2 px-1">Free AI SEO Tools</span>
                </div>
              </div>

              <h1 className="text-title mt-6 text-4xl font-medium max-w-3xl sm:mt-12 sm:text-5xl lg:text-5xl">
                Free GEO and AI SEO Tools for Your Business
              </h1>

              <p className="text-lg pt-3 max-w-3xl text-muted-foreground">
                Optimize your business for ChatGPT, Claude, Perplexity, and Gemini AI search. Use our free tools to check your AI SEO
                readiness, generate LLMs.txt files, create innovative tool ideas for your business, and improve how AI assistants recommend
                your products and services. We also offer free tools to optimize your business for Google search.
              </p>
            </div>
          </div>
        </section>

        {/* Tools Grid Section */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div className="relative border-x border-gray-950/[0.07] px-4 py-16 sm:px-12">
              <div className="grid gap-6 md:grid-cols-2">
                {toolsData.map((tool, index) => (
                  <ToolCard key={index} tool={tool} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* AI SEO FAQ Section */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div className="relative border-x border-gray-950/[0.07] px-4 py-16 sm:px-12">
              <h2 className="text-2xl font-semibold mb-8 text-center">
                How to Optimize for AI Search (ChatGPT, Claude, Gemini, Perplexity)
              </h2>
              <div className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Why optimize for ChatGPT and Claude?</h3>
                  <p className="text-muted-foreground text-sm">
                    ChatGPT, Claude, and Perplexity are becoming primary search tools for millions of users. When someone asks ChatGPT about
                    products in your industry, you want your business to be recommended. Our AI SEO tools help ensure ChatGPT and Claude
                    accurately present your offerings.
                  </p>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">What is AI SEO for ChatGPT?</h3>
                  <p className="text-muted-foreground text-sm">
                    AI SEO is the practice of optimizing your website so AI assistants like ChatGPT, Claude, Gemini, and Perplexity can
                    better understand and recommend your business. This includes structured data, clear content, and files like LLMs.txt
                    that AI models can parse.
                  </p>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">How do I appear in ChatGPT search results?</h3>
                  <p className="text-muted-foreground text-sm">
                    To appear when users search in ChatGPT or ask Claude questions, your website needs clear, structured content that AI can
                    understand. Use our free AI SEO audit to check your ChatGPT visibility and get specific recommendations for improvement.
                  </p>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Do Claude and Perplexity use the same optimization?</h3>
                  <p className="text-muted-foreground text-sm">
                    While ChatGPT, Claude, Perplexity, and Gemini have different architectures, they all benefit from similar optimization
                    strategies. Clear content structure, semantic HTML, and files like LLMs.txt help all AI assistants better understand
                    your business.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div className="px-5 sm:px-20 border-x border-gray-950/[0.07] hover:!bg-ui relative p-8 shadow-lg shadow-gray-900/5 before:absolute before:inset-1 before:rounded-b-lg before:bg-gradient-to-t before:from-blue-200 group-hover:bg-transparent">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="mb-6 md:mb-0 z-40">
                  <h3 className="text-xl font-semibold mb-2">Monitor Your AI Search Performance</h3>
                  <p className="text-gray-800/80 max-w-xl">
                    Track how ChatGPT, Claude, Perplexity, and Gemini mention your brand in real-time. Get alerts when AI assistants
                    recommend competitors instead of you. Optimize your AI search presence with data-driven insights.
                  </p>
                </div>
                <div className="flex flex-row gap-4 z-40">
                  <Button
                    variant="outline"
                    onClick={() => {
                      posthog.capture("book_demo_button_clicked", {
                        source: "free_tools_index",
                      });
                      router.push("/book-a-demo");
                    }}
                  >
                    Book a Demo
                  </Button>
                  <Button
                    className="flex items-center gap-2"
                    onClick={() => {
                      posthog.capture("get_started_button_clicked", {
                        source: "free_tools_index",
                      });
                      router.push(clientEnv.NEXT_PUBLIC_APP_URL);
                    }}
                  >
                    Get Started <ArrowRight className="size-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </BaseLayout>
  );
};

export default FreeToolsIndex;
